import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {ErrorBox, StyledDialogTitle} from '../CustomProjectDetailsStyles'
import {CancelButton, SaveButton} from '../../ProjectDetails/ProjectDetailStyle'
import PropTypes from 'prop-types'
import ExpandAllIcon from '@mui/icons-material/UnfoldMore'
import CollapseAllIcon from '@mui/icons-material/UnfoldLess'
import renderTableRows from './renderTableRows'
import {Box, Dialog, IconButton, TableBody, TableHead, TableRow} from '@mui/material'
import {
  hasAccordions,
  checkAllRowsExpanded,
  collectAllIds,
  expandAllRows,
  hasDuplicateLabels,
  hasValidationErrors,
  hasConflictWithDefaultLabels,
  getCustomizationKey,
  hasChanges
} from './helper'
import {
  StyledDarkDot,
  StyledLightDot,
  StyledNoteSection,
  StyledTableHeaderDefault,
  StyledTableHeaderCustom,
  StyledTableHeaderActions,
  StyledTableContainer,
  StyledTable,
  StyledExpandCollapseButton,
  StyledDivider,
  StyledDialogContent,
  StyledNoteBox,
  StyledNoteContainer,
  StyledErrorBox,
  StyledHeaderContentBox,
  StyledRevertAllButton,
  StyledDialogActions,
  StyledRevertAllIconButton,
  StyledRevertAllHistoryIcon
} from './Styles'
import {useAdminPermission} from '../../../store/hooks/adminPermission'

const CustomizationDialog = ({open, data, section, setIsLabelCustomized, handleClose, initialExpandedRows}) => {
  const {customizationData, setCustomizationData} = useAdminPermission()
  const [customLabels, setCustomLabels] = useState({})
  const [originalCustomLabels, setOriginalCustomLabels] = useState({})
  const [expandedRows, setExpandedRows] = useState({})
  const [editingRowId, setEditingRowId] = useState(null)
  const [error, setError] = useState(null)
  const [errorRowId, setErrorRowId] = useState(null)
  const prevOpenRef = useRef(false)
  const hasAnyCustomizations = Object.values(customLabels || {})?.some((label) => label?.newValue?.trim())

  const customizationLabelKey = getCustomizationKey(section)

  // Synchronize expanded rows when dialog transitions from closed to open.
  // useLayoutEffect fires synchronously after DOM mutations (before paint),
  // preserving the same timing guarantee as the previous render-time setState
  // without violating render purity or causing loops in StrictMode.
  useLayoutEffect(() => {
    if (open && !prevOpenRef.current) {
      setExpandedRows(initialExpandedRows || {})
    }
    prevOpenRef.current = open
  }, [open, initialExpandedRows])

  useEffect(() => {
    if (customizationData && section) {
      const existingCustomLabels = customizationData?.[customizationLabelKey] || {}
      setCustomLabels(existingCustomLabels)
      setOriginalCustomLabels(existingCustomLabels)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, customizationData])

  useEffect(() => {
    if (open) {
      const existingCustomLabels = customizationData?.[customizationLabelKey] || {}

      setCustomLabels(existingCustomLabels)
      setOriginalCustomLabels(existingCustomLabels)
      setError(null)
      setErrorRowId(null)
      setEditingRowId(null)
    }
  }, [open, customizationData, customizationLabelKey])

  useEffect(() => {
    if (error && !hasValidationErrors(error, customLabels, data)) {
      setError(null)
      setErrorRowId(null)
    }
  }, [customLabels, error, data])

  const handleResetLabel = (id) => {
    setCustomLabels((prev) => {
      const updated = {...prev}
      delete updated[id]
      return updated
    })
    setError(null)
    setErrorRowId(null)
    if (editingRowId === id) {
      setEditingRowId(null)
    }
  }

  const handleResetAll = () => {
    setCustomLabels({})
    setError(null)
    setErrorRowId(null)
    setEditingRowId(null)
  }

  const handleCloseDialog = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    setCustomLabels(originalCustomLabels)
    setExpandedRows({})
    setError(null)
    setErrorRowId(null)
    setEditingRowId(null)
    handleClose(event, reason)
  }

  const handleCancel = () => {
    setCustomLabels(originalCustomLabels)
    setExpandedRows({})
    setError(null)
    setErrorRowId(null)
    setEditingRowId(null)
    handleClose()
  }

  const validateLabel = (id, value) => {
    const tempCustomLabels = {
      ...customLabels,
      [id]: {
        newValue: value
      }
    }

    if (hasDuplicateLabels(tempCustomLabels) || hasConflictWithDefaultLabels(tempCustomLabels, data)) {
      setError('*Label already exists. Please choose a different name')
      setErrorRowId(id)
    } else if (value.length > 50) {
      setError(`*Exceeds the maximum character limit(${value.length}/50)`)
      setErrorRowId(id)
    } else {
      setError(null)
      setErrorRowId(null)
    }
  }

  const handleCustomLabelChange = (id, value, item) => {
    validateLabel(id, value)
    setCustomLabels((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        originalValue: prev[id]?.originalValue || item?.name,
        oldValue: prev[id]?.oldValue || '',
        name: item?.name,
        key: prev[id]?.key || item?.key || item?.id,
        id: prev[id]?.id || item?.id,
        type: prev[id]?.type || item?.type || 'filter',
        isTitle: prev[id]?.isTitle ?? item?.isTitle ?? false,
        newValue: value
      }
    }))
  }

  const handleExpandCollapseClick = () => {
    const allRows = [...data]
    const allIds = collectAllIds(allRows)
    const isAllExpanded = allIds.every((id) => expandedRows[id])

    if (isAllExpanded) {
      setExpandedRows({})
    } else {
      const expandedState = expandAllRows(allRows, {})
      setExpandedRows(expandedState)
    }
  }

  const handleSave = () => {
    setIsLabelCustomized(true)

    // Get all keys from the current section's data (including section titles)
    const collectKeys = (items, keys = []) => {
      for (const item of items) {
        // Collect both filter keys and section title keys
        if (item.id) {
          keys.push(item.id)
        } else if (item.name && item.isTitle) {
          // For section titles, use the name as the key
          keys.push(item.name)
        }
        if (item.children && item.children.length > 0) {
          collectKeys(item.children, keys)
        }
      }
      return keys
    }
    const currentSectionKeys = collectKeys(data)

    // Helper function to check if a label should be included
    const shouldIncludeLabel = (label) => {
      if (!label) return false
      // Section titles - only include if has custom value
      if (label.isTitle && label.type === 'sectionTitle') {
        return Boolean(label.newValue?.trim())
      }
      // Main title - always include; Regular filters - include if has value
      return label.isTitle || Boolean(label.newValue?.trim())
    }

    // For filterCustomLabels, preserve labels from OTHER sections
    let updatedSectionLabels = {}
    if (customizationLabelKey === 'filterCustomLabels') {
      // Keep all existing labels
      updatedSectionLabels = {...customizationData[customizationLabelKey]}

      // Remove labels from current section (both filters and section titles)
      for (const key of currentSectionKeys) {
        delete updatedSectionLabels[key]
      }

      // Add updated labels from current section
      for (const key of Object.keys(customLabels || {})) {
        if (shouldIncludeLabel(customLabels[key])) {
          updatedSectionLabels[key] = {
            ...customizationData[customizationLabelKey]?.[key],
            ...customLabels[key]
          }
        }
      }
    } else {
      // For other sections (usecases, personas, poi), update normally
      updatedSectionLabels = Object.keys(customLabels || {}).reduce((acc, item) => {
        acc[item] = {
          ...customizationData[customizationLabelKey]?.[item],
          ...customLabels[item]
        }
        return acc
      }, {})
    }

    const updatedCustomizationData = {
      ...customizationData,
      [customizationLabelKey]: updatedSectionLabels
    }
    setCustomizationData(updatedCustomizationData)

    setOriginalCustomLabels(customLabels)
    setExpandedRows({})
    setError(null)
    setErrorRowId(null)
    setEditingRowId(null)
    handleClose()
  }

  return (
    <Dialog maxWidth='md' fullWidth open={open} onClose={handleCloseDialog}>
      <StyledDialogTitle>
        Customize Labels
        <IconButton onClick={handleCloseDialog}>
          <CloseIcon variant='popOverCloseIcon' />
        </IconButton>
      </StyledDialogTitle>
      <StyledDivider />
      <StyledDialogContent>
        <StyledNoteBox>
          <StyledNoteContainer>
            <b>Note:</b>
            <StyledNoteSection>
              <StyledDarkDot /> &nbsp; Dot means the label is customized.
            </StyledNoteSection>
            <StyledNoteSection>
              <StyledLightDot /> &nbsp; Dot helps you find customized labels.
            </StyledNoteSection>
          </StyledNoteContainer>
          <StyledRevertAllButton disableRipple onClick={handleResetAll} disabled={!hasAnyCustomizations}>
            Revert all
            <StyledRevertAllIconButton disabled={!hasAnyCustomizations}>
              <StyledRevertAllHistoryIcon />
            </StyledRevertAllIconButton>
          </StyledRevertAllButton>
        </StyledNoteBox>
        <StyledTableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <StyledTableHeaderDefault>
                  <StyledHeaderContentBox>
                    <span>Default Label</span>
                    {hasAccordions(data) &&
                      (() => {
                        const isAllExpanded = checkAllRowsExpanded(data, expandedRows)
                        return (
                          <StyledExpandCollapseButton
                            disableRipple
                            variant='CustomSettings'
                            size='small'
                            onClick={handleExpandCollapseClick}
                          >
                            {isAllExpanded ? (
                              <>
                                Collapse All <CollapseAllIcon />
                              </>
                            ) : (
                              <>
                                Expand All <ExpandAllIcon />
                              </>
                            )}
                          </StyledExpandCollapseButton>
                        )
                      })()}
                  </StyledHeaderContentBox>
                </StyledTableHeaderDefault>
                <StyledTableHeaderCustom>Custom Label - Overwrite</StyledTableHeaderCustom>
                <StyledTableHeaderActions>Actions</StyledTableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableRows(
                data,
                {
                  customLabels,
                  handleCustomLabelChange,
                  handleResetLabel,
                  expandedRows,
                  setExpandedRows,
                  editingRowId,
                  setEditingRowId,
                  error,
                  errorRowId
                },
                {},
                0
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledErrorBox>{error && <ErrorBox color='error'>{error}</ErrorBox>}</StyledErrorBox>
        <Box>
          <CancelButton variant='contrastButton' onClick={handleCancel}>
            Cancel
          </CancelButton>
          <SaveButton
            disabled={hasValidationErrors(error, customLabels, data) || !hasChanges(customLabels, originalCustomLabels)}
            onClick={() => handleSave()}
          >
            Apply and Close
          </SaveButton>
        </Box>
      </StyledDialogActions>
    </Dialog>
  )
}

CustomizationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  section: PropTypes.string.isRequired,
  setIsLabelCustomized: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialExpandedRows: PropTypes.object
}

export default CustomizationDialog
